<template>
  <div class="savings">
    <div class="savings__container">
      <p class="savings__title">
        <slot name="title-savings"></slot>
      </p>
      <p class="savings__description">
        <slot name="description-savings"></slot>
      </p>
    </div>

    <!-- Grid Display -->
    <ul :class="['savings__grid', { 'savings__grid--popup': allowPopup }]">
      <li v-for="(value, key) in filteredSavingsData" :key="key" class="savings__item">
        <p class="savings__label">{{ formatLabel(key) }}</p>
        <p class="savings__value">
          {{ formatValue(key, value) }}
        </p>
      </li>
    </ul>

    <!-- Button to view complete list (when allowPopup is true) -->
    <button
      v-if="allowPopup && hasCopperPriceData"
      class="savings__view-button"
      @click="openPopup"
      type="button"
      aria-haspopup="dialog"
      :aria-expanded="isPopupOpen.toString()"
      :aria-label="copperDetailsLabel"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="savings__view-icon"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" /><path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" /><path d="M9 12l.01 0" /><path d="M13 12l2 0" /><path d="M9 16l.01 0" /><path d="M13 16l2 0" /></svg>
      <span>{{ copperDetailsLabel }}</span>
    </button>

    <!-- Scroll Popup for All Savings Details (only when allowPopup is true) -->
    <nt-scroll-popup
      v-if="allowPopup && hasCopperPriceData"
      ref="copperPopup"
      :default-open="false"
      :default-show-fab="false"
      :enable-fab="enableFab"
      :scroll-trigger="0"
      :auto-open-on-scroll="autoOpenOnScroll"
      :fab-label="copperDetailsLabel"
      body-align="left"
      @open="isPopupOpen = true"
      @close="isPopupOpen = false"
    >
      <template #title>{{ copperPopupTitle }}</template>
      <template #text>
        <ul class="savings__popup-list">
          <li v-for="item in allPopupData" :key="item.key" class="savings__popup-item">
            <span class="savings__popup-label">{{ item.label }}</span>
            <span class="savings__popup-value">{{ item.value }}</span>
          </li>
        </ul>
      </template>
    </nt-scroll-popup>
  </div>
</template>

<script>
import NtScrollPopup from '../../03_molecules/nt-scroll-popup/nt-scroll-popup.vue'

export default {
  name: 'calculator-savings',
  components: {
    NtScrollPopup,
  },
  data() {
    return {
      isPopupOpen: false,
    }
  },
  props: {
    savingsData: {
      type: Object,
      required: true,
    },
    allowPopup: {
      type: Boolean,
      default: true,
    },
    copperPopupTitle: {
      type: String,
      default: 'Copper Price Information',
    },
    copperDetailsLabel: {
      type: String,
      default: 'View complete Copper savings',
    },
    enableFab: {
      type: Boolean,
      default: false,
    },
    autoOpenOnScroll: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    filteredSavingsData() {
      // When allowPopup is false, show all data in the grid
      if (!this.allowPopup) {
        const data = { ...this.savingsData }

        if (data.CopperPriceValue !== null && data.CopperPriceValue !== undefined) {
          data.CopperPriceValue = data.CopperPriceUnit
            ? `${data.CopperPriceValue} ${data.CopperPriceUnit}`
            : data.CopperPriceValue
        }
        delete data.CopperPriceUnit

        return data
      }

      const excludedKeys = [
        'CopperPriceName',
        'CopperPriceUnit',
        'CopperPriceValue',
        'CopperPriceDate'
      ]

      return Object.keys(this.savingsData)
        .filter(key => !excludedKeys.includes(key))
        .reduce((obj, key) => {
          obj[key] = this.savingsData[key]
          return obj
        }, {})
    },
    allPopupData() {
      // Combine all savings data with copper details in one array
      const items = []

      // Add all savings data excluding fields already shown in the main grid and copper detail fields
      const excludedKeys = [
        'SavingsInVolume',
        'SavingsInCopper',
        'TotalSavedInCopperValue',
        'CopperPriceName',
        'CopperPriceUnit',
        'CopperPriceValue',
        'CopperPriceDate'
      ]

      Object.keys(this.savingsData).forEach(key => {
        if (!excludedKeys.includes(key)) {
          const value = this.savingsData[key]
          if (value !== null && value !== undefined) {
            items.push({
              key,
              label: this.formatLabel(key),
              value
            })
          }
        }
      })

      // Add copper price details at the end
      if (this.savingsData.CopperPriceName) {
        items.push({ key: 'CopperPriceName', label: 'Reference', value: this.savingsData.CopperPriceName })
      }
      if (this.savingsData.CopperPriceValue) {
        const value = this.savingsData.CopperPriceUnit
          ? `${this.savingsData.CopperPriceValue} ${this.savingsData.CopperPriceUnit}`
          : this.savingsData.CopperPriceValue
        items.push({ key: 'CopperPriceValue', label: 'Value', value })
      }
      if (this.savingsData.CopperPriceDate) {
        items.push({ key: 'CopperPriceDate', label: 'Month reference', value: this.savingsData.CopperPriceDate })
      }

      return items
    },
    hasCopperPriceData() {
      return !!(
        this.savingsData.CopperPriceName ||
        this.savingsData.CopperPriceUnit ||
        this.savingsData.CopperPriceValue ||
        this.savingsData.CopperPriceDate
      )
    }
  },
  methods: {
    formatLabel(key) {
      if (key === 'TotalSavedInCopperValue') {
        return 'Total Savings in Copper'
      }

      if (key === 'CopperPriceDate') {
        return 'Month reference'
      }

      if (key === 'CopperPriceName') {
        return 'Reference'
      }

      const words = key.split(/(?=[A-Z])/)
      const label = words[words.length - 1]

      if (label === 'Volume') {
        return `${label} (%)`
      } else if (label === 'Copper' || 'Cooper') {
        return `${label} (g)`
      }

      return label
    },
    openPopup() {
      if (this.$refs.copperPopup) {
        this.$refs.copperPopup.open()
      }
    },
    formatValue(key, value) {
      if (value === null || value === undefined) {
        return 'N/A'
      }

      if (key === 'TotalSavedInCopperValue') {
        const numericValue = parseFloat(value)
        return isNaN(numericValue) ? value : `$${numericValue.toFixed(2)}`
      }

      return value
    }
  },
}
</script>

<style lang="scss" scoped>
.savings {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 0.125rem solid #9ddda9;
  border-top: 0;
  padding: 2rem 1.5rem;

  @media screen and (min-width: 768px) {
    padding-inline: 1.5rem;
  }

  @media screen and (min-width: 1200px) {
    grid-area: 2 / 2 / 3 / 3;
    gap: 0;
  }

  &__container {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__title {
    font-weight: 700;
    font-size: 1rem;
    margin-bottom: 0.25rem;
    color: #454545;
  }

  &__description {
    font-size: 0.875rem;
    color: #454545;
    text-align: center;

    @media screen and (min-width: 768px) {
      text-align: left;
    }
  }

  &__grid {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    width: 100%;
    margin-top: 0.5rem;

    @media screen and (min-width: 768px) {
      display: grid;
      grid-template-columns: repeat(3, auto);
    }

    &--popup {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;

      @media screen and (min-width: 768px) {
        flex-direction: row;
        align-items: center;
      }
    }
  }

  &__item {
    display: flex;
    align-items: center;
  }

  &__item::before {
    content: "";
    
    @media screen and (min-width: 768px) {
      content: "|";
      margin-right: 0.5rem;
      color: #ccc;
    }
  }

  &__item:last-child::after {
    content: "";

    @media screen and (min-width: 768px) {
      content: "|";
      margin-left: 0.5rem;
      color: #ccc;
    }
  }

  &__grid:not(&__grid--popup) &__item::before,
  &__grid:not(&__grid--popup) &__item:last-child::after {
    content: none;
  }

  &__grid:not(&__grid--popup) &__item {
    @media screen and (min-width: 768px) {
      border-right: 1px solid #ccc;
    }
  }

  &__grid--popup &__item:first-child::before {
    content: "";
    margin-right: 0;
  }

  &__grid--popup &__item:last-child::after {
    content: "";
    margin-left: 0;
  }

  &__label {
    font-size: 0.875rem;
    color: #737373;
  }

  &__value {
    font-weight: 700;
    font-size: 0.875rem;
    color: #454545;
    margin-left: 0.25rem;
  }

  &__view-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 1rem;
    background-color: #77b883;
    color: #fff;
    font-weight: 700;
    font-size: 0.875rem;
    padding: 0.6rem 1.25rem;
    border: none;
    border-radius: 3.125rem;
    cursor: pointer;
    transition: all 0.3s ease;
    width: 100%;

    &:hover {
      background-color: #66a372;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    &:active {
      background-color: #5a9166;
    }

    &:focus-visible {
      outline: 0.125rem solid #454545;
      outline-offset: 0.125rem;
    }

    @media screen and (min-width: 768px) {
      width: auto;
    }
  }

  &__view-icon {
    width: 1.125rem;
    height: 1.125rem;
    flex-shrink: 0;
  }

  &__copper-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 1rem;
    background-color: #77b883;
    color: #fff;
    font-weight: 700;
    font-size: 0.875rem;
    padding: 0.6rem 1rem;
    border: none;
    border-radius: 3.125rem;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background-color: #66a372;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    &:active {
      background-color: #5a9166;
    }

    @media screen and (min-width: 768px) {
      width: 50%;
    }
  }

  &__copper-icon {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }

  &__copper-label {
    font-size: 0.875rem;
  }

  &__copper-value {
    font-weight: 700;
    font-size: 1rem;
  }

  &__popup-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    list-style: none;
    padding: 0;
    margin: 0;
    margin-top: 1rem;
  }

  &__popup-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__popup-label {
    font-weight: 400;
    color: #666666;
    font-size: 0.875rem;
    line-height: 1.2;
  }

  &__popup-value {
    font-weight: 700;
    color: #454545;
    font-size: 0.875rem;
    line-height: 1.2;
  }

  &__copper-details {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    list-style: none;
    padding: 0;
    margin: 0;
    margin-top: 1.5rem;
  }

  &__copper-item {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  &__copper-detail-label {
    font-weight: 400;
    color: #737373;
    font-size: 0.875rem;
    line-height: 100%;
    letter-spacing: 0.01em;
  }

  &__copper-detail-value {
    font-weight: 700;
    color: #454545;
    font-size: 0.875rem;
    line-height: 100%;
  }
}
</style>
